import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, Breadcrumbs, Link,
  CircularProgress, Alert, Divider, IconButton, Tooltip,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';

import { filesAPI } from '../../services/api';

/**
 * Diálogo para elegir una carpeta ya existente en Drive.
 *
 * Sólo muestra carpetas: se navega hasta la que se quiere y se confirma con
 * "Seleccionar esta carpeta", que devuelve el id de la carpeta actual.
 */
export default function DriveFolderPicker({
  open, onClose, onSelect, rootPath = null, title = 'Seleccionar carpeta',
  description = null, footer = null, confirmLabel = 'Seleccionar esta carpeta',
}) {
  const [data, setData] = useState(null);
  const [currentPath, setCurrentPath] = useState(rootPath);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (path) => {
    setLoading(true);
    setError(null);
    try {
      const r = await filesAPI.browse(path);
      setData(r.data);
      setCurrentPath(r.data.path);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al leer la carpeta');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load(rootPath);
  }, [open, rootPath, load]);

  const folders = (data?.entries || []).filter((e) => e.type === 'dir');
  const breadcrumbs = data?.breadcrumbs || [];

  const handleConfirm = async () => {
    if (!currentPath) return;
    setSaving(true);
    try {
      await onSelect(currentPath);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { height: '75vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' }}>
        {description && (
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Volver al inicio">
            <span>
              <IconButton size="small" onClick={() => load(rootPath)} disabled={loading}>
                <HomeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Subir un nivel">
            <span>
              <IconButton size="small" onClick={() => load(data.parent_path)}
                disabled={!data?.parent_path || loading}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Actualizar">
            <span>
              <IconButton size="small" onClick={() => load(currentPath)} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Breadcrumbs sx={{ flexGrow: 1 }} maxItems={5}>
            <Link component="button" variant="body2" underline="hover" onClick={() => load(rootPath)}>
              Inicio
            </Link>
            {breadcrumbs.map((c, i) => (
              i === breadcrumbs.length - 1 ? (
                <Typography key={c.path} variant="body2" fontWeight="medium">{c.label}</Typography>
              ) : (
                <Link key={c.path} component="button" variant="body2" underline="hover"
                  onClick={() => load(c.path)}>
                  {c.label}
                </Link>
              )
            ))}
          </Breadcrumbs>
          {loading && <CircularProgress size={18} />}
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box sx={{ flexGrow: 1, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {!loading && folders.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                Esta carpeta no tiene subcarpetas. Puedes seleccionarla tal cual.
              </Typography>
            </Box>
          )}
          <List dense disablePadding>
            {folders.map((f, i) => (
              <React.Fragment key={f.path}>
                {i > 0 && <Divider component="li" />}
                <ListItem disablePadding>
                  <ListItemButton onClick={() => load(f.path)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <FolderIcon sx={{ color: '#f5a623' }} />
                    </ListItemIcon>
                    <ListItemText primary={f.name} />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Box>
        {footer}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm}
          disabled={!currentPath || loading || saving}>
          {saving ? 'Trabajando…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
